import React, { useRef, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #6D83F2, #A6C5F2)",
    color: "#fff",
    textAlign: "center",
    padding: "2rem",
  },
  image: {
    width: "60%",
    maxWidth: "400px",
    marginBottom: "1.5rem",
  },
  heading: {
    fontSize: "2.5rem",
    fontWeight: "bold",
    marginBottom: "1rem",
  },
  message: {
    fontSize: "1.2rem",
    lineHeight: "1.6",
    marginBottom: "2rem",
  },
  button: {
    padding: "0.8rem 2rem",
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: "#ff6b6b",
    color: "#fff",
    border: "none",
    borderRadius: "25px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#ff4b4b",
      transform: "scale(1.05)",
    },
  },
  accordion: {
    width: "100%",
    maxWidth: "600px",
    marginTop: "2rem",
    backgroundColor: "#ffffff",
    color: "#000",
    borderRadius: "8px",
  },
  accordionSummary: {
    fontWeight: "bold",
  },
  errorStack: {
    whiteSpace: "pre-wrap",
    fontSize: "0.9rem",
    textAlign: "justify",
    width: "100%",
  },
});

const ErrorBoundaryWrapper = ({ children }) => {
  const errorImage = `${process.env.PUBLIC_URL}/assets/error-illustration.svg`;
  const [hasError, setHasError] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const classes = useStyles();

  const handleGoHome = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/";
  };

  const handleCatch = (error, errorInfo) => {
    setHasError(true);

    if (window.location.hostname === "localhost") {
      setErrorDetails({ error, errorInfo });
    }
  };

  if (hasError) {
    return (
      <div className={classes.root}>
        <img src={errorImage} alt="Erro inesperado" className={classes.image} />
        <h1 className={classes.heading}>Oops! Algo deu errado...</h1>
        <p className={classes.message}>
          Parece que encontramos um problema inesperado. Por favor, clique no
          botão abaixo para voltar à página inicial.
        </p>
        <button className={classes.button} onClick={handleGoHome}>
          Voltar para a Home
        </button>

        {window.location.hostname === "localhost" && errorDetails && (
          <Accordion className={classes.accordion}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.accordionSummary}
            >
              Detalhes do erro
            </AccordionSummary>
            <AccordionDetails>
              <pre className={classes.errorStack}>
                <strong>Erro:</strong> {errorDetails.error.toString()}
                <br />
                <strong>Stack Trace:</strong>{" "}
                {errorDetails.errorInfo.componentStack}
              </pre>
            </AccordionDetails>
          </Accordion>
        )}
      </div>
    );
  }

  return (
    <ErrorBoundary
      fallback={(error, errorInfo) => handleCatch(error, errorInfo)}
    >
      {children}
    </ErrorBoundary>
  );
};

class ErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const { fallback } = this.props;
    if (fallback) {
      fallback(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

export default ErrorBoundaryWrapper;
